import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card, Badge } from "@/components/primitives";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Upload, Download, Trash2, RotateCcw, FileText, History } from "lucide-react";

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "") + "/api";

export default function DocumentsTab({ entityType, entityId, canUpload = true, canDelete = true, canRestore = true }) {
  const [docs, setDocs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showDeleted, setShowDeleted] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [category, setCategory] = useState("");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [versionsFor, setVersionsFor] = useState(null);
  const [versions, setVersions] = useState([]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/documents?entity_type=${entityType}&entity_id=${entityId}&include_deleted=${showDeleted}&only_latest=true`);
      setDocs(data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [entityId, entityType, showDeleted]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/master/document-categories/?page_size=100");
        setCategories(data.items);
      } catch { /* noop */ }
    })();
  }, []);

  const upload = async () => {
    if (!file) { toast.error("Choose a file"); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("entity_type", entityType);
      fd.append("entity_id", entityId);
      if (category) fd.append("category_code", category);
      if (description) fd.append("description", description);
      await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded");
      setUploadOpen(false); setFile(null); setCategory(""); setDescription("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setSubmitting(false);
  };

  const remove = async (id) => {
    try { await api.delete(`/documents/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const restore = async (id) => {
    try { await api.post(`/documents/${id}/restore`); toast.success("Restored"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const download = (id, filename) => {
    const url = `${API_BASE}/documents/${id}/download`;
    const a = document.createElement("a");
    a.href = url; a.target = "_blank"; a.rel = "noopener";
    document.body.appendChild(a); a.click(); a.remove();
  };

  const showVersions = async (d) => {
    setVersionsFor(d);
    try {
      const { data } = await api.get(`/documents/${d.id}/versions`);
      setVersions(data.items);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-3" data-testid="documents-tab">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-[11px] mono text-hocp-textMuted">
          <FileText className="w-3 h-3" />
          {docs.length} document(s)
          <label className="flex items-center gap-1 ml-3 text-hocp-textMuted cursor-pointer">
            <input type="checkbox" checked={showDeleted} onChange={(e) => setShowDeleted(e.target.checked)} data-testid="show-deleted"/>
            Show deleted
          </label>
        </div>
        {canUpload && (
          <Button size="sm" onClick={() => setUploadOpen(true)} data-testid="upload-doc-button">
            <Upload className="w-3 h-3 mr-1.5"/>Upload
          </Button>
        )}
      </div>

      <Card className="overflow-hidden">
        <table className="w-full">
          <thead className="bg-hocp-bgCard border-b border-hocp-border">
            <tr>{["Filename", "Category", "Version", "Size", "Uploaded By", "Date", ""].map(h => (
              <th key={h} className="px-3 py-2 text-[10px] uppercase tracking-[0.1em] text-hocp-textMuted mono text-left">{h}</th>
            ))}</tr>
          </thead>
          <tbody>
            {docs.map(d => (
              <tr key={d.id} data-testid={`doc-row-${d.id}`} className={`border-b border-hocp-border last:border-0 ${d.is_deleted ? "opacity-50" : ""}`}>
                <td className="px-3 py-2 text-[13px] text-hocp-textPrimary truncate max-w-[280px]" title={d.filename}>{d.filename}</td>
                <td className="px-3 py-2"><Badge tone="neutral">{d.category_code || "—"}</Badge></td>
                <td className="px-3 py-2 mono text-[12px] text-hocp-textMuted">v{d.version}</td>
                <td className="px-3 py-2 mono text-[12px] text-hocp-textMuted">{(d.size / 1024).toFixed(1)} KB</td>
                <td className="px-3 py-2 text-[12px] text-hocp-textMuted">{d.uploader_email}</td>
                <td className="px-3 py-2 mono text-[11px] text-hocp-textMuted">{(d.created_at || "").slice(0, 16).replace("T", " ")}</td>
                <td className="px-3 py-2 text-right space-x-1">
                  <Button size="sm" variant="ghost" onClick={() => showVersions(d)} title="Versions" data-testid={`versions-${d.id}`}><History className="w-3 h-3"/></Button>
                  <Button size="sm" variant="ghost" onClick={() => download(d.id, d.filename)} data-testid={`download-${d.id}`}><Download className="w-3 h-3"/></Button>
                  {d.is_deleted ? (canRestore && <Button size="sm" variant="ghost" onClick={() => restore(d.id)} data-testid={`restore-${d.id}`}><RotateCcw className="w-3 h-3"/></Button>)
                              : (canDelete && <Button size="sm" variant="ghost" onClick={() => remove(d.id)} data-testid={`delete-${d.id}`}><Trash2 className="w-3 h-3 text-red-400"/></Button>)}
                </td>
              </tr>
            ))}
            {!docs.length && <tr><td colSpan={7} className="text-center text-hocp-textMuted py-6 text-sm">No documents</td></tr>}
          </tbody>
        </table>
      </Card>

      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent className="bg-hocp-bgCard border border-hocp-border">
          <DialogHeader><DialogTitle>Upload Document</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input type="file" onChange={(e) => setFile(e.target.files?.[0])} data-testid="upload-file"/>
            <Select value={category || "none"} onValueChange={(v) => setCategory(v === "none" ? "" : v)}>
              <SelectTrigger data-testid="upload-category"><SelectValue placeholder="Category (optional)"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— None —</SelectItem>
                {categories.map(c => <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input placeholder="Description (optional)" value={description} onChange={(e) => setDescription(e.target.value)}/>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadOpen(false)}>Cancel</Button>
            <Button onClick={upload} disabled={submitting} data-testid="confirm-upload">{submitting ? "..." : "Upload"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!versionsFor} onOpenChange={(v) => !v && setVersionsFor(null)}>
        <DialogContent className="bg-hocp-bgCard border border-hocp-border max-w-xl">
          <DialogHeader><DialogTitle>Versions — {versionsFor?.filename}</DialogTitle></DialogHeader>
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {versions.map(v => (
              <div key={v.id} className="flex items-center justify-between border-b border-hocp-border py-2">
                <div>
                  <div className="text-[13px] text-hocp-textPrimary">v{v.version} — {v.filename}</div>
                  <div className="text-[11px] mono text-hocp-textMuted">{(v.created_at || "").slice(0, 16).replace("T", " ")} · {v.uploader_email}</div>
                </div>
                <Button size="sm" variant="outline" onClick={() => download(v.id, v.filename)}><Download className="w-3 h-3"/></Button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
